import React, { useRef, useState, useEffect, useCallback } from "react";
import * as THREE from "three";
import { useThree } from "@react-three/fiber";
import DraggableObject from "./DraggableObject";
import { RectStairsType, TriangleSymbolType, zIndexEpsilon } from "../../types/floorplannerTypes";
import { SelectableSymbol, BoundingBox } from "./SelectableSymbol";
import { projectToWorld } from "./projectToWorld";
import { FloorplannerStoreContext } from "../../store/floorplannerStore";
import { Line } from "@react-three/drei";
import { runInAction, set } from "mobx";
import { editorStore } from "../../store/editorStore";
import { observer } from "mobx-react-lite";
import PatternFillMesh from "./PatternFillMesh";
import ShapeProjectionOverlay from "./ShapeProjectionOverlay";
import { shouldShowShapePrimitiveProjection } from "../../features/floorplan3d/symbols/primitives/shapes/shapePrimitiveGeometry";
import { canvasAccentColor } from "../../constants/brandColors";
import { resolveFillOpacity } from "../../utils/fillOpacity";
import { getOpacityRemountEpoch } from "../../utils/opacityRemount";
import { isSymbolAspectRatioLocked } from "../../utils/symbolAspectRatio";
import {
  isSymbolAbovePlanCutPlane,
  PLAN_OVERHEAD_DASH,
} from "../../utils/planCutPlane";

interface TriangleSymbolProps {
  triangle: TriangleSymbolType;
  onDragStart: (triangle: TriangleSymbolType, offset: [number, number]) => void;
  onDrag: (newPosition: [number, number]) => void;
  onDragEnd: (endPosition: [number, number]) => void;
}

const TriangleSymbol: React.FC<TriangleSymbolProps> = observer(({
  triangle,
  onDragStart,
  onDragEnd,
  onDrag,
}) => {
  const { gl, camera } = useThree();
  const groupRef = useRef<THREE.Group>(null);
  const floorplannerStore = React.useContext(FloorplannerStoreContext);

  const lineWeight = triangle.lineWeight || floorplannerStore.symbolLineWeight;
  const [triangleWidth, setTriangleWidth] = useState(triangle.triangleWidth);
  const [triangleHeight, setTriangleHeight] = useState(triangle.triangleHeight);
  const [localPosition, setLocalPosition] = useState<[number, number]>([triangle.position.x, triangle.position.y]);

  const grabHandleSize = 0.1; // Variable to control the size of the grab handle
  const flipX = triangle.flipHorizontal ? -1 : 1; // Flip horizontally
  const flipY = triangle.flipVertical ? -1 : 1; // Flip vertically
  // Center and pivot are at origin since bounding box is centered
  const centerPosition: [number, number] = [0, 0];
  const lineColor = triangle.lineColor || floorplannerStore.lineColor;
  const pivot: [number, number] = [0, 0];
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false);
  const noopRaycast = () => null;
  const aboveCutPlane = isSymbolAbovePlanCutPlane(triangle);
  const projectionActive =
    shouldShowShapePrimitiveProjection(triangle) && !aboveCutPlane;
  const useDashed =
    projectionActive || triangle.lineType === "dashed";
  const fillOpacity = resolveFillOpacity(triangle);
  const fillIsTransparent = fillOpacity < 1;

  // Calculate the bounding box of the triangle - returns corners relative to center (0,0)
  const calculateBoundingBox = useCallback((): BoundingBox => {
    if (!groupRef.current) {
      return {
        topLeft: [0, 0],
        topRight: [0, 0],
        bottomLeft: [0, 0],
        bottomRight: [0, 0],
        width: 0,
        height: 0,
        depth: 0,
      };
    }

    // Clone the object
    const tempGroup = groupRef.current.clone();

    // Save the original rotation
    const originalRotation = tempGroup.rotation.clone();

    // Temporarily reset the rotation to 0
    tempGroup.rotation.set(0, 0, 0);

    // Calculate the bounding box as if the object had no rotation
    const boundingBox = new THREE.Box3().setFromObject(tempGroup);
    const size = new THREE.Vector3();
    boundingBox.getSize(size);

    // Restore the original rotation
    tempGroup.rotation.copy(originalRotation);

    // Add line weight adjustment
    const lineWeightAdjustment = lineWeight / 100;
    const width = size.x + lineWeightAdjustment;
    const height = size.y + lineWeightAdjustment;
    const halfWidth = width / 2;
    const halfHeight = height / 2;

    // Return corners relative to center (0, 0) - consistent with how SvgSymbol works
    return {
      topLeft: [-halfWidth, halfHeight],
      topRight: [halfWidth, halfHeight],
      bottomLeft: [-halfWidth, -halfHeight],
      bottomRight: [halfWidth, -halfHeight],
      width: width,
      height: height,
      depth: 0,
    };
  }, [triangle.position.x, triangle.position.y, triangle.flipHorizontal, triangle.flipVertical, triangleWidth, triangleHeight, lineWeight]);

  const dragOffset = useRef({ x: 0, y: 0 });

  const onHandleDrag = (event: PointerEvent) => {
    const [worldX, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
    const dy = worldY - triangle.position.y - dragOffset.current.y;
    // Position is handled by the store, no longer need to update centerPosition
  };

  const onPointerUpHandle = () => {
    gl.domElement.removeEventListener("pointermove", onHandleDrag);
    gl.domElement.removeEventListener("pointerup", onPointerUpHandle);
    floorplannerStore.setBlockDirty(false);
    floorplannerStore.setDirty();
  };

  const handleDrag = (newPosition: [number, number]) => {
    onDrag(newPosition);
  };
  // Call the function and destructure the returned values
  //const { rectPoints, extraLines } = generateRectPointsAndLines();

  // Handle Drag Top (Red Line)
  const dragOffsetTop = useRef({ x: 0, y: 0 });

  const onHandleDragTop = (event: PointerEvent) => {
    const [, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
    const dy = worldY - triangle.position.y - dragOffsetTop.current.y;
    floorplannerStore.updateSymbolProperty(triangle.id, "triangleHeight", Math.max(0.1, triangleHeight + dy * flipY));
  };

  const onPointerUpHandleTop = () => {
    gl.domElement.removeEventListener("pointermove", onHandleDragTop);
    gl.domElement.removeEventListener("pointerup", onPointerUpHandleTop);
    floorplannerStore.setBlockDirty(false);
    floorplannerStore.updateSymbolProperty(triangle.id, "triangleHeight", triangleHeight);
    floorplannerStore.setDirty();
  };
  const [geometry, setGeometry] = useState<THREE.BufferGeometry | undefined>(undefined);

  // Function to create the triangle geometry
  const createTriangleGeometry = () => {
    const vertices = new Float32Array([
      0, triangleHeight / 2, 0,                     // Top vertex
      -triangleWidth / 2, -triangleHeight / 2, 0,   // Bottom-left vertex
      triangleWidth / 2, -triangleHeight / 2, 0,    // Bottom-right vertex
    ]);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));

    return geometry;
  };

  // Update geometry whenever triangleWidth or triangleHeight changes
  useEffect(() => {
    setGeometry(createTriangleGeometry());
  }, [triangleWidth, triangleHeight]);

  useEffect(() => {
    editorStore.updateGroupRef(triangle.id, groupRef.current);
  }, [editorStore, triangle.id]);

  useEffect(() => {
    if (isResizing) return;
    setTriangleWidth(triangle.triangleWidth);
    setTriangleHeight(triangle.triangleHeight);
    setLocalPosition([triangle.position.x, triangle.position.y]);
  }, [isResizing, triangle.triangleWidth, triangle.triangleHeight, triangle.position.x, triangle.position.y]);

  // Redraw when the symbol.selected changes
  useEffect(() => {
  }, [triangle.selected]);

  return (
    <group position={[0, 0, (triangle.zIndex * zIndexEpsilon)]}>
      <ShapeProjectionOverlay symbol={triangle} />
      <DraggableObject
        position={localPosition}
        onDragStart={(offset) => {
          setIsDragging(true);
          onDragStart(triangle, offset)
        }}
        onDragEnd={(endPosition) => {
          onDragEnd(endPosition)
          setIsDragging(false);
        }}
        onDrag={handleDrag}
        selectable={true}
        attachmentId={triangle.id}
        attachmentType="noAttachments"
        symbol={triangle}
      >
        <SelectableSymbol
          handleSize={floorplannerStore.symbolHandleSize}
          calculateBoundingBox={calculateBoundingBox}
          onResizeStart={() => setIsResizing(true)}
          onResize={(newWidth, newHeight, _boundingBox, _handle, newPosition) => {
            setTriangleWidth(newWidth);
            setTriangleHeight(newHeight);
            if (newPosition) {
              setLocalPosition(newPosition);
            }
          }}
          onResizeEnd={(_event, finalResize) => {
            setIsResizing(false);
            if (!finalResize) return;
            floorplannerStore.updateSymbolProperty(triangle.id, "triangleWidth", finalResize.width);
            floorplannerStore.updateSymbolProperty(triangle.id, "triangleHeight", finalResize.height);
            if (finalResize.position) {
              floorplannerStore.updateSymbolProperty(triangle.id, "position", finalResize.position);
            }
          }}
          center={centerPosition}
          rotation={triangle.rotation}
          symbol={triangle}
          drawHandles={true}
          pivot={pivot}
          onPointerOver={() => setIsHovered(true)}
          onPointerOut={() => setIsHovered(false)}
          isDragging={isDragging}
          lockAspectRatio={isSymbolAspectRatioLocked(triangle.lockAspectRatio)}
        >
          <group
            // Remount when opacity / dash style changes — drei Line often ignores live dash updates.
            key={`triangle-opacity-${Math.round(fillOpacity * 100)}-${useDashed ? "dashed" : "solid"}-${getOpacityRemountEpoch(triangle.id)}`}
            ref={groupRef}
            rotation={[0, 0, triangle.rotation || 0]}
            onPointerEnter={() => {
              if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.add("cursor-vector");
            }}
            onPointerLeave={() => {
              if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.remove("cursor-vector");
            }}
          >
            {/* Draw triangle */}
            <mesh geometry={geometry}>
              <PatternFillMesh
                fillColor={triangle.fillColor || 0xffffff}
                fillPattern={triangle.fillPattern}
                useWorldSpace={false}
                side={THREE.DoubleSide}
                opacity={projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity}
                transparent={projectionActive || fillIsTransparent}
                depthWrite={!projectionActive && !fillIsTransparent}
              />
            </mesh>

            {/* Render triangle outline */}
            <Line
              key={`triangle-outline-${Math.round((projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity) * 100)}-${useDashed ? "dashed" : "solid"}`}
              points={[
                [0, triangleHeight / 2, 0], // Top
                [-triangleWidth / 2, -triangleHeight / 2, 0], // Bottom-left
                [triangleWidth / 2, -triangleHeight / 2, 0], // Bottom-right
                [0, triangleHeight / 2, 0], // Close the triangle (back to Top)
              ]}
              color={projectionActive ? 0x94a3b8 : canvasAccentColor(triangle.selected, !!isHovered, lineColor)}
              lineWidth={projectionActive ? 0.8 : lineWeight}
              opacity={projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity}
              transparent={projectionActive || fillIsTransparent}
              depthWrite={!projectionActive && !fillIsTransparent}
              {...(useDashed ? PLAN_OVERHEAD_DASH : {})}
              raycast={noopRaycast}
            />
          </group>
        </SelectableSymbol>
      </DraggableObject>
    </group>
  );
});

export default TriangleSymbol;
